// components/hauler/HaulerMap.js
//
// @deprecated 2026-05-02 — extracted for potential re-use; not currently
// mounted. The Route tab no longer surfaces a map (zone-first model
// puts the spatial discovery surface on the Jobs tab via JobsMap).
// `jobCoords` is still imported by RouteScreen for navigateUrl. The
// default-export component (the claimed-pin map itself) is dormant and
// safe to delete if no map ever returns to /haulers Route. Kept in the
// repo as a known-good extraction so a future re-introduction doesn't
// require recovering the mapReady fix from git history.
//
// Mapbox map for the Hauler Route tab.
//
// Reuses the script-injection pattern from src/pages/DriverMapScreen.js
// (additive — that file is frozen). Reads the access token from the
// REACT_APP_MAPBOX_TOKEN env var via constants.js. If the token is
// missing or the Mapbox script fails to load, the component renders
// nothing and the parent RouteScreen falls back to the text stop list.
//
// Behavior:
//   - centers on Hill AFB / market center when no stops have coords
//   - fits bounds to claimed stops with valid coords
//   - drops a numbered marker at each stop
//   - missing coords on a single stop never crash the map
//
// IC compliance: the map is informational. No forced order, no
// bundled-job semantics, no dispatch coercion.

import React, { useEffect, useRef, useState } from "react";
import { MAPBOX_TOKEN } from "../../constants";

// Hill AFB / Boyer Hill center. [lng, lat] order per Mapbox.
const DEFAULT_CENTER = [-112.002, 41.109];
const MAPBOX_VERSION = "v3.11.0";

// Hauler-side map style. Defaults to the trashdash custom Mapbox style;
// override at build time with REACT_APP_MAPBOX_HAULER_STYLE if needed.
// The retired DriverMapScreen.js uses its own hardcoded style — do NOT
// consolidate here.
const HAULER_MAP_STYLE =
  process.env.REACT_APP_MAPBOX_HAULER_STYLE ||
  "mapbox://styles/trashdash/cmohmfrbc002601ss5in5as4e";

let _mapboxLoaderPromise = null;
function loadMapbox() {
  if (typeof window === "undefined") return Promise.reject(new Error("no_window"));
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
  if (_mapboxLoaderPromise) return _mapboxLoaderPromise;
  _mapboxLoaderPromise = new Promise((resolve, reject) => {
    const cssId = "mapbox-gl-css";
    if (!document.getElementById(cssId)) {
      const link = document.createElement("link");
      link.id = cssId;
      link.rel = "stylesheet";
      link.href = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_VERSION}/mapbox-gl.css`;
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_VERSION}/mapbox-gl.js`;
    script.async = true;
    script.onload = () => resolve(window.mapboxgl);
    script.onerror = () => reject(new Error("mapbox_script_failed"));
    document.head.appendChild(script);
  });
  return _mapboxLoaderPromise;
}

// Tolerant coord extraction — mirrors DriverMapScreen.js field fallbacks.
export function jobCoords(job) {
  const lat = Number(job?.lat ?? job?.latitude ?? job?.pickup_lat ?? job?.pickup_latitude);
  const lng = Number(job?.lng ?? job?.longitude ?? job?.pickup_lng ?? job?.pickup_longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

// Phase 4.5: pin style depends on geocoding accuracy. 'address' is
// per-house (rooftop). Anything else (street, place, missing, or the
// street_center fallback when Mapbox failed) is approximate — render
// a softer pin with a "~" prefix and tooltip so the Hauler doesn't
// drive to the wrong house thinking the pin is exact.
function jobGeocodeAccuracy(job) {
  if (job?.geocode_accuracy === "address") return "address";
  return "approximate";
}

export default function HaulerMap({ stops = [], height = 240, onError }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [unavailable, setUnavailable] = useState(false);
  // mapReady flips true on the Mapbox `load` event so the marker effect
  // below knows when it can safely place markers + pan. Without this gate
  // the marker effect can run synchronously on mount BEFORE Mapbox has
  // finished loading the script (mapRef.current is still null), return
  // early, and never re-run because `stops` doesn't change again on a
  // single-stop route. The result was a blank-pin map with the camera
  // stuck on Hill AFB default. See diagnosis log 2026-05-02.
  const [mapReady, setMapReady] = useState(false);

  // Mount + load Mapbox once
  useEffect(() => {
    if (!MAPBOX_TOKEN) {
      setUnavailable(true);
      onError && onError("missing_token");
      return;
    }
    if (!containerRef.current) return;

    let cancelled = false;
    loadMapbox()
      .then((mapboxgl) => {
        if (cancelled || !containerRef.current) return;
        mapboxgl.accessToken = MAPBOX_TOKEN;
        const map = new mapboxgl.Map({
          container: containerRef.current,
          style: HAULER_MAP_STYLE,
          center: DEFAULT_CENTER,
          zoom: 13,
          attributionControl: false,
        });
        mapRef.current = map;
        // Wait for the style to finish loading before marking ready.
        // The marker useEffect below gates on `mapReady` and re-runs
        // when this flips, so the first paint of markers + camera is
        // never lost to a race.
        map.on("load", () => { if (!cancelled) setMapReady(true); });
      })
      .catch((err) => {
        setUnavailable(true);
        onError && onError(err?.message || "mapbox_load_failed");
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (_) {}
        mapRef.current = null;
      }
    };
    // Mount-once: onError is intentionally captured at first render so the
    // parent re-creating the callback doesn't re-mount Mapbox.
  }, []);// eslint-disable-line

  // Update markers + bounds whenever stops change OR when the map first
  // becomes ready (the first stops value may have arrived before load).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (typeof window === "undefined" || !window.mapboxgl) return;

    // Clear previous markers stored on the map ref
    if (map._haulerMarkers) {
      map._haulerMarkers.forEach((m) => { try { m.remove(); } catch (_) {} });
    }
    map._haulerMarkers = [];

    const validStops = stops
      .map((stop, i) => ({ stop, i, coord: jobCoords(stop) }))
      .filter(s => s.coord);

    if (validStops.length === 0) {
      try { map.setCenter(DEFAULT_CENTER); map.setZoom(13); } catch (_) {}
      return;
    }

    const bounds = new window.mapboxgl.LngLatBounds();
    validStops.forEach(({ stop, i, coord }) => {
      const isAddress = jobGeocodeAccuracy(stop) === "address";
      const el = document.createElement("div");
      if (isAddress) {
        // Sharp pin: solid sky, white border, full opacity.
        el.style.cssText = `
          width: 28px; height: 28px; border-radius: 50%;
          background: #3D90D9; color: #fff;
          display: flex; align-items: center; justify-content: center;
          font: 700 12px/1 'Sora', system-ui, sans-serif;
          border: 2px solid #fff;
          box-shadow: 0 2px 6px rgba(0,0,0,.35);
        `;
        el.textContent = String(i + 1);
      } else {
        // Approximate pin: larger, dashed amber ring, semi-transparent
        // sky fill, "~" prefix to match the estimated-value convention.
        el.style.cssText = `
          width: 36px; height: 36px; border-radius: 50%;
          background: rgba(61,144,217,.55); color: #fff;
          display: flex; align-items: center; justify-content: center;
          font: 700 11px/1 'Sora', system-ui, sans-serif;
          border: 2px dashed #F29727;
          box-shadow: 0 2px 8px rgba(0,0,0,.30);
        `;
        el.textContent = `~${i + 1}`;
        el.title = "Approximate location — exact address pending";
      }
      const marker = new window.mapboxgl.Marker(el)
        .setLngLat([coord.lng, coord.lat])
        .addTo(map);
      map._haulerMarkers.push(marker);
      bounds.extend([coord.lng, coord.lat]);
    });

    if (validStops.length === 1) {
      const { coord } = validStops[0];
      try { map.setCenter([coord.lng, coord.lat]); map.setZoom(14); } catch (_) {}
    } else {
      try { map.fitBounds(bounds, { padding: 40, duration: 600, maxZoom: 15 }); } catch (_) {}
    }
  }, [stops, mapReady]);

  if (unavailable) return null;

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height,
        borderRadius: 14,
        overflow: "hidden",
        background: "var(--hl-bg)",
        border: "1px solid var(--hl-border)",
      }}
    />
  );
}
